import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface GatewaySummary {
  total: number;
  online: number;
  offline: number;
  stale: number;
  degraded: number;
  unassigned: number;
  provisioned_awaiting_commission: number;
  provisional_properties_awaiting_gateway: number;
}

export interface GatewayDevice {
  id: string;
  claim_code: string | null;
  property_id: string | null;
  property_name: string | null;
  org_name: string | null;
  lifecycle_status: string;
  operational_status: string;
  last_heartbeat_at: string | null;
  firmware_version: string | null;
  wifi_rssi: number | null;
  bus_devices_expected: number | null;
  bus_devices_responding: number | null;
  data_source_mode: string | null;
  uptime_seconds: number | null;
}

export interface DataSourceMismatch {
  property_id: string;
  property_name: string | null;
  org_name: string | null;
  data_source_mode: string;
  has_gateway: boolean;
  mismatch_reason: string;
}

export interface GatewayCommand {
  id: string;
  device_id: string;
  claim_code: string | null;
  command_type: string;
  category: string | null;
  status: string;
  created_at: string;
  executed_at: string | null;
}

export interface GatewayDeployment {
  id: string;
  name: string | null;
  firmware_version: string | null;
  status: string;
  total_gateways: number | null;
  updated_gateways: number | null;
  failed_gateways: number | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
}

export interface GatewayDiagnosticsData {
  summary: GatewaySummary;
  devices: GatewayDevice[];
  data_source_mismatches: DataSourceMismatch[];
  failed_commands: GatewayCommand[];
  pending_commands: GatewayCommand[];
  recent_deployments: GatewayDeployment[];
}

async function fetchGatewayDiagnostics(): Promise<GatewayDiagnosticsData> {
  const { data, error } = await supabase.rpc('admin_gateway_diagnostics');
  if (error) throw error;
  return data as GatewayDiagnosticsData;
}

export function useAdminGatewayDiagnostics() {
  return useQuery({
    queryKey: ['admin-gateway-diagnostics'],
    queryFn: fetchGatewayDiagnostics,
  });
}
